import { apiClient } from '../utils/api-client';
import { throwForResponse } from './api-error';
import type {
  CompetitionRound,
  RoundClimb,
  RoundResult,
  CreateRoundData,
  UpdateRoundData,
  RoundBlockCount,
  CreateRoundClimbData,
  UpdateRoundClimbData,
  CreateBoulderResultData,
  CreateLeadResultData,
  RoundStatus,
  UnclassedBlock
} from '../types/competition-round';

interface RoundsListResponse {
  data: CompetitionRound[];
}

interface RoundResponse {
  data: CompetitionRound;
}

interface RoundClimbsListResponse {
  data: RoundClimb[];
}

interface RoundClimbResponse {
  data: RoundClimb;
}

interface RoundResultsListResponse {
  data: RoundResult[];
}

interface RoundResultResponse {
  data: RoundResult;
}

class CompetitionRoundsService {
  // ========== Rounds ==========

  async getRounds(competitionId: string): Promise<CompetitionRound[]> {
    const response = await apiClient.get(`/api/competitions/${competitionId}/rounds`);

    if (!response.ok) {
      throw new Error('Failed to fetch rounds');
    }

    const data: RoundsListResponse = await response.json();
    return data.data;
  }

  /**
   * `forUserId` asks for a ward's card instead of the viewer's: the round is
   * the same, `my_result` on each block is that child's. The server only
   * honours an account the viewer is guardian of.
   */
  async getRound(competitionId: string, roundId: string, forUserId?: string | null): Promise<CompetitionRound> {
    const query = forUserId ? `?for_user_id=${encodeURIComponent(forUserId)}` : '';
    const response = await apiClient.get(`/api/competitions/${competitionId}/rounds/${roundId}${query}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Round not found');
      }
      throw new Error('Failed to fetch round');
    }

    const data: RoundResponse = await response.json();
    return data.data;
  }

  async createRound(competitionId: string, roundData: CreateRoundData): Promise<CompetitionRound> {
    const response = await apiClient.post(`/api/competitions/${competitionId}/rounds`, {
      round: roundData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to create round');
    }

    const data: RoundResponse = await response.json();
    return data.data;
  }

  async updateRound(competitionId: string, roundId: string, roundData: UpdateRoundData): Promise<CompetitionRound> {
    const response = await apiClient.patch(`/api/competitions/${competitionId}/rounds/${roundId}`, {
      round: roundData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to update round');
    }

    const data: RoundResponse = await response.json();
    return data.data;
  }

  /**
   * Another round set up like this one — the next turn. Only the name and the
   * planned start are sent: everything else is copied from the original on the
   * server, and the copy starts as a draft.
   */
  async duplicateRound(
    competitionId: string,
    roundId: string,
    roundData: { name: string; start_time: string | null }
  ): Promise<CompetitionRound> {
    const response = await apiClient.post(`/api/competitions/${competitionId}/rounds/${roundId}/duplicate`, {
      round: roundData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to duplicate round');
    }

    const data: RoundResponse = await response.json();
    return data.data;
  }

  async deleteRound(competitionId: string, roundId: string): Promise<void> {
    const response = await apiClient.delete(`/api/competitions/${competitionId}/rounds/${roundId}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to delete round');
    }
  }

  /**
   * Says go. `durationMinutes` overrides what the round was planned at —
   * omitted, the round keeps its own, and a round with none runs until somebody
   * closes it by hand.
   */
  /**
   * Puts the round back to not-started. The clock always goes; the results only
   * when `clearResults` says so, because neither answer is safe to assume.
   */
  async resetRound(
    competitionId: string,
    roundId: string,
    clearResults: boolean
  ): Promise<CompetitionRound> {
    const response = await apiClient.post(
      `/api/competitions/${competitionId}/rounds/${roundId}/reset`,
      { clear_results: clearResults }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to reset round');
    }

    const data: RoundResponse = await response.json();
    return data.data;
  }

  async startRound(
    competitionId: string,
    roundId: string,
    durationMinutes?: number
  ): Promise<CompetitionRound> {
    const response = await apiClient.post(
      `/api/competitions/${competitionId}/rounds/${roundId}/start`,
      durationMinutes === undefined ? {} : { duration_minutes: durationMinutes }
    );

    if (!response.ok) {
      // Through `throwForResponse` rather than a bare Error because the *code*
      // matters here: a lead round refused for `routes_missing_holds` gets an
      // alert naming the routes, which is not a toast.
      await throwForResponse(response, 'Failed to start round');
    }

    const data: RoundResponse = await response.json();
    return data.data;
  }

  /**
   * Stops the clock. The climbing ends here and the round goes to its
   * corrections — everybody who could write on a card still can.
   */
  async finishRound(competitionId: string, roundId: string): Promise<CompetitionRound> {
    const response = await apiClient.post(`/api/competitions/${competitionId}/rounds/${roundId}/finish`, {});

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to finish round');
    }

    const data: RoundResponse = await response.json();
    return data.data;
  }

  /**
   * The moves with nothing to carry: to the check (`verification`, where only
   * the judges and the organisers write) and back to the athletes'
   * corrections. Through the generic status door, which asks the server's
   * machine (`Lifecycle::Round#transition_status!`).
   */
  async updateRoundStatus(competitionId: string, roundId: string, status: RoundStatus): Promise<CompetitionRound> {
    const response = await apiClient.patch(`/api/competitions/${competitionId}/rounds/${roundId}/status`, {
      data: { attributes: { status } },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to change the round status');
    }

    const data: RoundResponse = await response.json();
    return data.data;
  }

  /**
   * The last ending, from verification, and this panel's alone: after it
   * nothing in the member app writes on the round.
   */
  async closeRound(competitionId: string, roundId: string): Promise<CompetitionRound> {
    const response = await apiClient.post(`/api/competitions/${competitionId}/rounds/${roundId}/close`, {});

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to close round');
    }

    const data: RoundResponse = await response.json();
    return data.data;
  }

  async reorderRounds(competitionId: string, positions: string[]): Promise<CompetitionRound[]> {
    const response = await apiClient.patch(`/api/competitions/${competitionId}/rounds/reorder`, {
      positions
    });

    if (!response.ok) {
      throw new Error('Failed to reorder rounds');
    }

    const data: RoundsListResponse = await response.json();
    return data.data;
  }

  /**
   * Sets how many numbered blocks each class climbs. The server creates or
   * removes `round_climbs` from the end until each class has what was asked,
   * naming the new ones after their ordinal ("Blocco 1"…).
   *
   * Sent as a whole because that is how the panel edits it — one form, one
   * save. A class left out keeps the blocks it has. Removing a block that has
   * already been judged is refused rather than done quietly.
   */
  async setBlockCounts(
    competitionId: string,
    roundId: string,
    blocks: RoundBlockCount[]
  ): Promise<CompetitionRound> {
    const response = await apiClient.put(
      `/api/competitions/${competitionId}/rounds/${roundId}/blocks`,
      { blocks }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to save the blocks');
    }

    const data: RoundResponse = await response.json();
    return data.data;
  }

  // ========== Round Climbs ==========

  async getRoundClimbs(competitionId: string, roundId: string): Promise<RoundClimb[]> {
    const response = await apiClient.get(`/api/competitions/${competitionId}/rounds/${roundId}/climbs`);

    if (!response.ok) {
      throw new Error('Failed to fetch round climbs');
    }

    const data: RoundClimbsListResponse = await response.json();
    return data.data;
  }

  async addClimbToRound(
    competitionId: string,
    roundId: string,
    climbData: CreateRoundClimbData
  ): Promise<RoundClimb> {
    const response = await apiClient.post(
      `/api/competitions/${competitionId}/rounds/${roundId}/climbs`,
      { round_climb: climbData }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to add climb to round');
    }

    const data: RoundClimbResponse = await response.json();
    return data.data;
  }

  async updateRoundClimb(
    competitionId: string,
    roundId: string,
    roundClimbId: string,
    climbData: UpdateRoundClimbData
  ): Promise<RoundClimb> {
    const response = await apiClient.patch(
      `/api/competitions/${competitionId}/rounds/${roundId}/climbs/${roundClimbId}`,
      { round_climb: climbData }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to update round climb');
    }

    const data: RoundClimbResponse = await response.json();
    return data.data;
  }

  /**
   * A problem with cards on it is refused unless `discardResults` says the cards
   * go too — and the server only allows that for a problem of no class.
   */
  async removeClimbFromRound(
    competitionId: string,
    roundId: string,
    roundClimbId: string,
    { discardResults = false }: { discardResults?: boolean } = {}
  ): Promise<void> {
    const query = discardResults ? '?discard_results=true' : '';
    const response = await apiClient.delete(
      `/api/competitions/${competitionId}/rounds/${roundId}/climbs/${roundClimbId}${query}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to remove climb from round');
    }
  }

  /** The round's problems of no class, with the cards on them — for the setup. */
  async getUnclassedBlocks(competitionId: string, roundId: string): Promise<UnclassedBlock[]> {
    const response = await apiClient.get(
      `/api/competitions/${competitionId}/rounds/${roundId}/climbs/unclassed`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the problems of no class');
    }

    const data: { data: UnclassedBlock[] } = await response.json();
    return data.data;
  }

  async reorderRoundClimbs(
    competitionId: string,
    roundId: string,
    positions: string[]
  ): Promise<RoundClimb[]> {
    const response = await apiClient.patch(
      `/api/competitions/${competitionId}/rounds/${roundId}/climbs/reorder`,
      { positions }
    );

    if (!response.ok) {
      throw new Error('Failed to reorder round climbs');
    }

    const data: RoundClimbsListResponse = await response.json();
    return data.data;
  }

  // ========== Round Results ==========

  async getRoundClimbResults(
    competitionId: string,
    roundId: string,
    roundClimbId: string
  ): Promise<RoundResult[]> {
    const response = await apiClient.get(
      `/api/competitions/${competitionId}/rounds/${roundId}/climbs/${roundClimbId}/results`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch results');
    }

    const data: RoundResultsListResponse = await response.json();
    return data.data;
  }

  async createBoulderResult(
    competitionId: string,
    roundId: string,
    roundClimbId: string,
    resultData: CreateBoulderResultData
  ): Promise<RoundResult> {
    const response = await apiClient.post(
      `/api/competitions/${competitionId}/rounds/${roundId}/climbs/${roundClimbId}/results`,
      { result: resultData }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to create result');
    }

    const data: RoundResultResponse = await response.json();
    return data.data;
  }

  async createLeadResult(
    competitionId: string,
    roundId: string,
    roundClimbId: string,
    resultData: CreateLeadResultData
  ): Promise<RoundResult> {
    const response = await apiClient.post(
      `/api/competitions/${competitionId}/rounds/${roundId}/climbs/${roundClimbId}/results`,
      { result: resultData }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to create result');
    }

    const data: RoundResultResponse = await response.json();
    return data.data;
  }

  async updateResult(
    competitionId: string,
    roundId: string,
    roundClimbId: string,
    resultId: string,
    resultData: Partial<CreateBoulderResultData | CreateLeadResultData>
  ): Promise<RoundResult> {
    const response = await apiClient.patch(
      `/api/competitions/${competitionId}/rounds/${roundId}/climbs/${roundClimbId}/results/${resultId}`,
      { result: resultData }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to update result');
    }

    const data: RoundResultResponse = await response.json();
    return data.data;
  }

  async deleteResult(
    competitionId: string,
    roundId: string,
    roundClimbId: string,
    resultId: string
  ): Promise<void> {
    const response = await apiClient.delete(
      `/api/competitions/${competitionId}/rounds/${roundId}/climbs/${roundClimbId}/results/${resultId}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to delete result');
    }
  }

  async validateResult(
    competitionId: string,
    roundId: string,
    roundClimbId: string,
    resultId: string
  ): Promise<RoundResult> {
    const response = await apiClient.post(
      `/api/competitions/${competitionId}/rounds/${roundId}/climbs/${roundClimbId}/results/${resultId}/validate`,
      {}
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to validate result');
    }

    const data: RoundResultResponse = await response.json();
    return data.data;
  }

  async invalidateResult(
    competitionId: string,
    roundId: string,
    roundClimbId: string,
    resultId: string
  ): Promise<RoundResult> {
    const response = await apiClient.delete(
      `/api/competitions/${competitionId}/rounds/${roundId}/climbs/${roundClimbId}/results/${resultId}/validate`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0]?.detail || 'Failed to invalidate result');
    }

    const data: RoundResultResponse = await response.json();
    return data.data;
  }
  // ========== Boulder Attempts ==========

  async addBoulderAttempt(
    competitionId: string,
    roundId: string,
    roundClimbId: string,
    resultId: string,
    attemptType: 'attempt' | 'zone' | 'top'
  ): Promise<RoundResult> {
    const response = await apiClient.post(
      `/api/competitions/${competitionId}/rounds/${roundId}/climbs/${roundClimbId}/results/${resultId}/attempts`,
      { attempt_type: attemptType }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0] || 'Failed to add attempt');
    }

    const data: RoundResultResponse = await response.json();
    return data.data;
  }

  async removeBoulderAttempt(
    competitionId: string,
    roundId: string,
    roundClimbId: string,
    resultId: string,
    attemptId: string
  ): Promise<RoundResult> {
    const response = await apiClient.delete(
      `/api/competitions/${competitionId}/rounds/${roundId}/climbs/${roundClimbId}/results/${resultId}/attempts/${attemptId}`
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.errors?.[0] || 'Failed to remove attempt');
    }

    const data: RoundResultResponse = await response.json();
    return data.data;
  }
}

export const competitionRoundsService = new CompetitionRoundsService();
